import { useCallback, useEffect, useState } from 'react';

/**
 * The cameras attached to the machine this page is running on.
 *
 * Ported from veriform-desktop's `cameraDeviceHelper` (renderer/utils). Asking
 * for permission first is what makes the list usable: without it
 * `enumerateDevices` still answers, but every label is blank, and the label is
 * the only part that identifies a camera to the capture (desktop matches it
 * against ffmpeg's device list).
 *
 * The embedded page only gets this far because the desktop app grants the frame
 * `allow="camera"`. In an ordinary browser tab the permission prompt is the
 * user's own, and a refusal leaves the list empty rather than failing.
 *
 * Desktop refreshes on its own USB-connected event; here the browser's
 * `devicechange` is the same signal and needs no plumbing.
 */

export interface CameraDevice {
  deviceId: string;
  label: string;
  groupId: string;
}

export interface CameraDevices {
  devices: CameraDevice[];
  loading: boolean;
  /** Set when the camera could not be reached at all -- usually a refusal. */
  denied: boolean;
}

export function useCameraDevices(enabled: boolean): CameraDevices {
  const [devices, setDevices] = useState<CameraDevice[]>([]);
  const [loading, setLoading] = useState(false);
  const [denied, setDenied] = useState(false);

  const read = useCallback(async () => {
    const media = navigator.mediaDevices;
    // Absent on an insecure origin, and in jsdom.
    if (!media?.enumerateDevices) {
      setDenied(true);
      return;
    }

    try {
      // Permission first: without it every label comes back blank.
      const stream = await media.getUserMedia({ video: true });
      stream.getTracks().forEach(track => track.stop());
      setDenied(false);
    } catch {
      setDenied(true);
    }

    try {
      const found = await media.enumerateDevices();
      setDevices(
        found
          .filter(device => device.kind === 'videoinput')
          .map(device => ({
            deviceId: device.deviceId,
            label: device.label || `Camera ${device.deviceId.slice(0, 8)}`,
            groupId: device.groupId,
          })),
      );
    } catch {
      setDevices([]);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;
    setLoading(true);
    void read().finally(() => { if (!cancelled) setLoading(false); });

    const media = navigator.mediaDevices;
    const onChange = () => { void read(); };
    media?.addEventListener?.('devicechange', onChange);

    return () => {
      cancelled = true;
      media?.removeEventListener?.('devicechange', onChange);
    };
  }, [enabled, read]);

  return { devices, loading, denied };
}
